import { useMemo } from "react";
import type { FieldValues } from "react-hook-form";
import type { Gender } from "@b2bportal/air-booking-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { nameRegex } from "@hopper-b2b/utilities";
import dayjs from "dayjs";
import { MINIMUM_DRIVER_AGE, TravelerEditMode } from "../types";
import { isValidAirTravelerName } from "../utils/isValidAirTravelerName";

export interface UseTravellerDetailsFormFieldsProps {
  dateFormat?: string;
  showGender?: boolean;
  showNationality?: boolean;
  requireGender?: boolean;
  requireNationality?: boolean;
  travelerEditMode?: TravelerEditMode;
  // When true (default), the nationality field is pre-filled with the brand's
  // preferred country code. Set false to leave the field empty so the user
  // must make an explicit choice — required by HSBC tenants (HSBC-1316).
  prefillNationality?: boolean;
  /** Require at least two non-space characters in first and last names. */
  enforceMinNameLength?: boolean;
}

export enum TravellerFormFields {
  FirstName = "firstName",
  MiddleName = "middleName",
  LastName = "lastName",
  DateOfBirth = "dateOfBirth",
  Gender = "gender",
  Nationality = "nationality",
}

export type TravellerDetailsFormContext = {
  [TravellerFormFields.FirstName]: string;
  [TravellerFormFields.MiddleName]: string;
  [TravellerFormFields.LastName]: string;
  [TravellerFormFields.DateOfBirth]: string;
  [TravellerFormFields.Gender]: Gender | undefined;
  [TravellerFormFields.Nationality]: string | undefined;
};

export const useTravellerDetailsFormFields = (
  props: UseTravellerDetailsFormFieldsProps = {}
) => {
  const {
    dateFormat = "DD/MM/YYYY",
    showGender = true,
    showNationality = false,
    requireGender = showGender,
    requireNationality = showNationality,
    travelerEditMode = TravelerEditMode.ALL,
    prefillNationality = true,
    enforceMinNameLength = false,
  } = props;
  const { t, brand } = useI18nContext();

  const travellerDetailsDefaultValues: TravellerDetailsFormContext = {
    [TravellerFormFields.FirstName]: "",
    [TravellerFormFields.MiddleName]: "",
    [TravellerFormFields.LastName]: "",
    [TravellerFormFields.DateOfBirth]: "",
    [TravellerFormFields.Gender]: undefined,
    [TravellerFormFields.Nationality]: prefillNationality
      ? brand.preferredCountryCode
      : "",
  };

  const commonNameValidationProperties = useMemo(
    () => ({
      pattern: {
        value: nameRegex,
        message: t("noSpecialCharactersError"),
      },
      maxLength: {
        value: 31,
        message: t("nameLengthExceedError"),
      },
    }),
    [t]
  );

  const getAirTravelerNameValidation = (errorMessage: string) =>
    enforceMinNameLength
      ? {
          required: errorMessage,
          validate: (name: string) =>
            isValidAirTravelerName(name) || errorMessage,
        }
      : {};

  const formFields: Partial<Record<TravellerFormFields, FieldValues>> = {
    [TravellerFormFields.FirstName]: {
      required: t("checkoutSection.travelerForm.errors.firstNameRequired"),
      ...commonNameValidationProperties,
      ...getAirTravelerNameValidation(
        t("apac.travelerForm.errors.firstNameMinLength")
      ),
    },
    [TravellerFormFields.MiddleName]: {
      required: false,
      ...commonNameValidationProperties,
    },
    [TravellerFormFields.LastName]: {
      required: t("checkoutSection.travelerForm.errors.lastNameRequired"),
      ...commonNameValidationProperties,
      ...getAirTravelerNameValidation(
        t("apac.travelerForm.errors.lastNameMinLength")
      ),
    },
    [TravellerFormFields.DateOfBirth]: {
      required: t("checkoutSection.travelerForm.errors.dateOfBirthRequired"),
      validate: (birthdate: string) => {
        const dateDayjs = dayjs(birthdate, dateFormat, true);
        if (!dateDayjs.isValid() || dateDayjs.isAfter(dayjs())) {
          return t("enterAValidBirthDate");
        }
        if (
          travelerEditMode === TravelerEditMode.CARS_ONLY &&
          dayjs().diff(dateDayjs, "year") < MINIMUM_DRIVER_AGE
        ) {
          return t("enterAValidBirthDate");
        }
      },
    },
  };
  if (showGender) {
    formFields[TravellerFormFields.Gender] = requireGender
      ? { required: t("checkoutSection.travelerForm.errors.genderRequired") }
      : {};
  } else {
    delete travellerDetailsDefaultValues[TravellerFormFields.Gender];
  }

  if (showNationality) {
    formFields[TravellerFormFields.Nationality] = requireNationality
      ? { required: t("travelerForm.errors.nationalityRequired") }
      : {};
  } else {
    delete travellerDetailsDefaultValues[TravellerFormFields.Nationality];
  }

  return { formFields, travellerDetailsDefaultValues };
};
